import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { withScrollTop } from '../../../../global/components/HOCs/with-scroll-Top';
import { withLayout } from '../../../../global/components/HOCs/with-layout';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { withForbidden } from '../../../../global/components/HOCs/with-forbidden';
import { TcsmFeatures } from '../../../../global/models/features-model';
import AccountInfoContainer from '../../components/account-info-container';
import { PersonalInformationApp } from '../personal-info/personal-info';
import { AccountInfoRoutes } from '../../constants/routes';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { MyRelationshipsApp } from '../relationships/my-relationships';
import { MaritalStatusApp } from '../marital-status/marital-status';
import { useMyCPGMidYearSettings } from './hooks/useMyCPGMidyearSettings';
import { TcsmAppConfigs } from '../../../../global/models/configuration-models';
import GenericStepper from '../../../../global/components/navigation/generic-stepper';
import { getMyCPGMidyearEnrollmentStepItems, getMyCPGMidyearEnrollmentSteps } from './helpers/mycpg-midyear-stepper-helpers';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { MyCPGMidYearProps } from './models/mycpg-midyear-epi-props';
import { NewHireEnrollmentRoutes } from '../../../arc/features/new-hire-enrollment/models/new-hire-routes';
import { MyCPGMidYearCoverage } from './mycpg-midyear-coverage';
import { MyMidYearPersonalInfoWizard } from './wizards/my-mid-year-personal-information-wizard';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { useQuery } from '../../../../global/hooks/useQuery';
import { DependentInfo } from '../dependents/dependent-info';
import { useBenefitEnrollmentStep } from '../../../arc/features/new-hire-enrollment/hooks/useBenefitEnrollmentStep';
import { clearNewHireEnrollmentState, setCurrentNewHireBenefitEnrollmentStep } from '../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { MyCPGMidYearConfirmation } from './mycpg-midyear-confirmation';
import { MemberNewHireWelcomePage } from '../../../arc/features/new-hire-enrollment/member/member-new-hire-landing';
import { useParticipantSettings } from '../../../arc/features/new-participant-enrollment/hooks/useParticipantSettings';
import { MyCpgMidYearPersonalInfoAlertTypes } from '../../../notification/notification-models';

const RelationshipsWrapper = withScrollTop()(withLayout(null, GpsType.MYCPG_MIDYEAR_RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer)));
const PersonalInfoWrapper = withScrollTop()(withLayout(null, GpsType.MYCPG_MIDYEAR_PERSONALINFO)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer)));
const MaritalStatusWrapper = withLayout(null, GpsType.MYCPG_MIDYEAR_MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(null, GpsType.MYCPG_MIDYEAR_DEPENDENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));


interface Props {
	appConfig: TcsmAppConfigs;
}

function MyCPGMidyearApp(props: Props) {

	const { appConfig } = props;
	const midyearLoaded = useMyCPGMidYearSettings(appConfig.myCpgMidYearSettingsId);
	const routeMatch = useRouteMatch();
	const settingsLoaded = useParticipantSettings(appConfig?.participantEnrollmentSettingsId, appConfig?.annualEnrollmentSettingsId, appConfig?.newHireSettingsId);
	const myCPGMidYearProps = useSelector<TcsmStates, MyCPGMidYearProps>(state => state?.epiProps?.myCpgMidyearSettingsState?.epiContent);
	const epiApiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const app = useContext(DemographicsContext);
	const dispatch = useDispatch();
	const history = useHistory();
	const query = useQuery()?.get('alerts');
	const mycpgRoot = useBasePath(`${AccountInfoRoutes.MyCpgRoot}`);
	const customWizard = new MyMidYearPersonalInfoWizard(epiApiState, AnnualEnrollmentKey.newHireEnrollment, app, dispatch, history, query?.split(',') as MyCpgMidYearPersonalInfoAlertTypes[], mycpgRoot);
	const coveragePath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.Midyear}${NewHireEnrollmentRoutes.Coverage}`);
	
	useEffect(() => {
		return function cleanUp() {
	
			dispatch(clearNewHireEnrollmentState());
		};
	}, []);


	const renderPreSubmissionRoutes = () => {
		return (
			<>
				<Route exact path={routeMatch.path} render={() => <MemberNewHireWelcomePage basePath={routeMatch.path} appSettings={appConfig} isMidYear />} />
				<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
					<MaritalStatusWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<MaritalStatusApp basePath={routeMatch.path} />
					</MaritalStatusWrapper>
				)} />
				<Route path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
					<PersonalInfoWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<PersonalInformationApp basePath={routeMatch.path} customPersonalInfoWizard={customWizard} />
					</PersonalInfoWrapper>
				)} />
				<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
					<RelationshipsWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<MyRelationshipsApp basePath={routeMatch.path} customNextStep={coveragePath} />
					</RelationshipsWrapper>
				)} />

				<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
					<DependentsWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} />
					</DependentsWrapper>
				)} />
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Coverage}`} render={() => <MyCPGMidYearCoverage />} />
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Confirmation}`} render={() => <MyCPGMidYearConfirmation settignsId={appConfig?.myCpgMidYearSettingsId} />} />

			</>
		);
	};

	if (!midyearLoaded || !settingsLoaded) {
		return <LoadingCircle />;
	}

	return (
		<>
			<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.newHireEnrollment}>
				<GenericStepper
					basePath={routeMatch.path}
					allSteps={getMyCPGMidyearEnrollmentSteps()}
					useCurrentStep={useBenefitEnrollmentStep}
					setCurrentStep={setCurrentNewHireBenefitEnrollmentStep}
					progressItems={getMyCPGMidyearEnrollmentStepItems(myCPGMidYearProps)}
				/>
				<Switch>


					{renderPreSubmissionRoutes()}
				</Switch>
			</AnnualEnrollmentContext.Provider>
		</>
	);
}

export default MyCPGMidyearApp;